/**
 * Payment methods: owner-provided Bit/PayBox links (no per-business API exists), cash at the venue, card via the
 * payments adapter. Deposit claims ("שילמתי") are confirmed or rejected by the owner.
 */
import { enabledOnlineMethods, holdExpiry } from '../policies';
import { formatNis } from '../money';
import { formatDate, formatTime, toMs } from '../time';
import {
  fail, ok, paymentMethodsOf, type Appointment, type Id, type Message, type OnlineMethod, type PaymentMethod, type PaymentMethods, type Result,
} from '../types';
import { loadBundle, notifyAppointment, type Deps } from './context';
import { localeTag } from '../../i18n';

/** A claimed deposit keeps the slot this long so the owner can check their Bit/PayBox account. */
export const CLAIM_HOLD_MIN = 12 * 60;
export const METHOD_LABEL: Record<OnlineMethod | 'cash' | 'other', string> = { card: 'כרטיס', bit: 'Bit', paybox: 'PayBox', cash: 'מזומן', other: 'אחר' };

export type PaymentWarning = 'deposit_ignored_no_online_method' | 'bit_needs_link_or_phone' | 'paybox_needs_link_or_phone' | 'no_method_enabled';

/** Settings validation: warnings never block saving. */
export function paymentWarnings(methods: PaymentMethods, depositServices: number): PaymentWarning[] {
  const out: PaymentWarning[] = [];
  const need = (m: PaymentMethods['bit']): boolean => m.enabled && !(m.link ?? '').trim() && !(m.phone ?? '').trim();
  if (need(methods.bit)) out.push('bit_needs_link_or_phone');
  if (need(methods.paybox)) out.push('paybox_needs_link_or_phone');
  if (!methods.cash && !methods.card && !methods.bit.enabled && !methods.paybox.enabled) out.push('no_method_enabled');
  if (depositServices > 0 && enabledOnlineMethods({ paymentMethods: methods }).length === 0) out.push('deposit_ignored_no_online_method');
  return out;
}

export function paymentUseCases(d: Deps) {
  const now = (): string => d.clock.nowIso();

  async function getPaymentSettings(businessId: Id): Promise<Result<{ methods: PaymentMethods; online: OnlineMethod[]; warnings: PaymentWarning[] }, 'business_not_found'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const methods = paymentMethodsOf(b.settings);
    return ok({ methods, online: enabledOnlineMethods(b.settings), warnings: paymentWarnings(methods, b.services.filter((s) => s.active && (s.depositAgorot ?? 0) > 0).length) });
  }

  /** Saves the methods (partial patch per method) and returns validation warnings. */
  async function updatePaymentMethods(
    businessId: Id, patch: { cash?: boolean; card?: boolean; bit?: Partial<PaymentMethods['bit']>; paybox?: Partial<PaymentMethods['paybox']> },
  ): Promise<Result<{ methods: PaymentMethods; warnings: PaymentWarning[] }, 'business_not_found' | 'invalid_link'>> {
    const b = await loadBundle(d, businessId);
    if (!b) return fail('business_not_found');
    const cur = paymentMethodsOf(b.settings);
    const clean = (m: PaymentMethods['bit']): PaymentMethods['bit'] => {
      const link = (m.link ?? '').trim();
      const phone = (m.phone ?? '').trim();
      return { enabled: m.enabled, ...(link ? { link } : {}), ...(phone ? { phone } : {}) };
    };
    const methods: PaymentMethods = {
      cash: patch.cash ?? cur.cash, card: patch.card ?? cur.card,
      bit: clean({ ...cur.bit, ...patch.bit }), paybox: clean({ ...cur.paybox, ...patch.paybox }),
    };
    for (const m of [methods.bit, methods.paybox]) if (m.link !== undefined && !/^https:\/\/[^\s]+$/i.test(m.link)) return fail('invalid_link');
    await d.storage.repo('settings').put({ ...b.settings, paymentMethods: methods, updatedAt: now() });
    return ok({ methods, warnings: paymentWarnings(methods, b.services.filter((s) => s.active && (s.depositAgorot ?? 0) > 0).length) });
  }

  /** Client taps "I paid" for a Bit/PayBox deposit (manage token required). Extends the hold so the owner has time to check. */
  async function claimDeposit(input: { appointmentId: Id; token: string }): Promise<Result<Appointment, 'appointment_not_found' | 'forbidden' | 'not_awaiting_deposit' | 'not_offline_method' | 'hold_expired'>> {
    const a = await d.storage.repo('appointments').get(input.appointmentId);
    if (!a) return fail('appointment_not_found');
    if (!input.token || input.token !== a.manageToken) return fail('forbidden');
    if (a.status !== 'awaiting_deposit') return fail('not_awaiting_deposit');
    if (a.depositMethod !== 'bit' && a.depositMethod !== 'paybox') return fail('not_offline_method');
    if (a.depositStatus === 'claimed') return ok(a);
    if (a.holdExpiresAt !== undefined && toMs(a.holdExpiresAt) <= d.clock.now()) return fail('hold_expired');
    const b = await loadBundle(d, a.businessId);
    if (!b) return fail('appointment_not_found');
    const t = now();
    const next: Appointment = { ...a, depositStatus: 'claimed', depositClaimedAt: t, holdExpiresAt: holdExpiry(d.clock.now(), { ...b.settings, depositHoldMin: CLAIM_HOLD_MIN }), updatedAt: t };
    await d.storage.repo('appointments').put(next);
    await notifyOwnerOfClaim(next);
    return ok(next);
  }

  async function notifyOwnerOfClaim(a: Appointment): Promise<Message | undefined> {
    const b = await loadBundle(d, a.businessId);
    const client = await d.storage.repo('clients').get(a.clientId);
    const service = b?.services.find((s) => s.id === a.serviceId);
    const to = b?.business.whatsapp ?? b?.business.phone;
    if (!b || !client || !service || !to || !a.depositMethod) return undefined;
    const tag = localeTag(b.business.locale);
    const ms = toMs(a.start);
    return d.messaging.send({
      businessId: b.business.id, to, clientId: client.id, appointmentId: a.id, purpose: 'reminders', dedupeKey: `deposit_claimed_owner:${a.id}`, scheduledFor: now(),
      call: { template: 'deposit_claimed_owner', params: {
        businessName: b.business.name, clientName: client.name.split(' ')[0] ?? client.name, serviceName: service.name, date: formatDate(ms, 'long', tag, b.business.tz),
        time: formatTime(ms, tag, b.business.tz), amount: formatNis(a.depositAgorot, tag), method: METHOD_LABEL[a.depositMethod],
      } },
    }).then(async (m) => (m.status === 'queued' && d.messaging.dispatchNow ? (await d.messaging.dispatchNow(m.id)) ?? m : m));
  }

  /** Owner confirms the deposit arrived (Bit/PayBox, or any method): the booking is confirmed and the client is told. */
  async function confirmDepositReceived(appointmentId: Id, method: PaymentMethod = 'bit'): Promise<Result<{ appointment: Appointment; messages: Message[] }, 'appointment_not_found' | 'not_awaiting_deposit'>> {
    const t = now();
    const next = await d.storage.transact(['appointments', 'settings'], async (tx) => {
      const cur = await tx.get('appointments', appointmentId);
      if (!cur) return 'appointment_not_found' as const;
      if (cur.status !== 'awaiting_deposit') return 'not_awaiting_deposit' as const;
      const settings = await tx.get('settings', cur.businessId);
      const { holdExpiresAt: _h, ...rest } = cur;
      const row: Appointment = {
        ...rest, status: settings?.approvalMode === 'manual' ? 'pending' : 'confirmed', depositStatus: 'paid', updatedAt: t,
        ...(method === 'bit' || method === 'paybox' || method === 'card' ? { depositMethod: method } : {}),
      };
      await tx.put('appointments', row);
      return row;
    });
    if (typeof next === 'string') return fail(next);
    return ok({ appointment: next, messages: await notifyAppointment(d, 'deposit_paid', next) });
  }

  /** Owner says the money did not arrive: the hold is released and the client is told (deposit_expired wording). */
  async function rejectDepositClaim(appointmentId: Id): Promise<Result<{ appointment: Appointment; messages: Message[] }, 'appointment_not_found' | 'not_awaiting_deposit'>> {
    const t = now();
    const next = await d.storage.transact(['appointments'], async (tx) => {
      const cur = await tx.get('appointments', appointmentId);
      if (!cur) return 'appointment_not_found' as const;
      if (cur.status !== 'awaiting_deposit') return 'not_awaiting_deposit' as const;
      const { holdExpiresAt: _h, ...rest } = cur;
      const row: Appointment = { ...rest, status: 'cancelled_owner', cancelReason: 'deposit_timeout', depositStatus: 'failed', cancelledAt: t, updatedAt: t };
      await tx.put('appointments', row);
      return row;
    });
    if (typeof next === 'string') return fail(next);
    return ok({ appointment: next, messages: await notifyAppointment(d, 'deposit_expired', next) });
  }

  return { getPaymentSettings, updatePaymentMethods, claimDeposit, confirmDepositReceived, rejectDepositClaim };
}
